// Entrar con enlace al correo. Solo correos dados de alta; la respuesta es la misma esté o no dado de alta.
// Proveedor firebase: el enlace de Firebase regresa aquí con ?mode=signIn&oobCode=…&apiKey=… y esta pantalla termina el
// acceso (REST de Identity Toolkit → ID token → POST /api/acceso/firebase). Proveedor local/smtp: el enlace va directo
// a /api/acceso/entrar, que pone la cookie y redirige a "/".
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { MailCheck } from 'lucide-react';
import { Correo } from '@cotizador/compartido';
import { api } from '../api/consultas.ts';
import { transporteSimulado } from '../api/cliente.ts';
import { Boton, CampoTexto, ErrorEnLinea } from '../componentes/ui.tsx';
import { Logotipo } from '../componentes/marca.tsx';
import { navegar, usarRuta } from '../ruteo.tsx';
import { usarSesion } from '../sesion.tsx';
import { FranjaApiSimulada } from '../componentes/franjas.tsx';

const LLAVE_CORREO = 'cotizador:correo-enlace';

function guardarCorreo(c: string): void {
  try {
    window.localStorage.setItem(LLAVE_CORREO, c);
  } catch {
    /* sin almacenamiento: se pide el correo otra vez al volver */
  }
}
function correoGuardado(): string {
  try {
    return window.localStorage.getItem(LLAVE_CORREO) ?? '';
  } catch {
    return '';
  }
}

export function Entrar() {
  const { recargar } = usarSesion();
  const { consulta } = usarRuta();
  const [correo, setCorreo] = useState(correoGuardado());
  const [errorCampo, setErrorCampo] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [respuesta, setRespuesta] = useState<{ mensaje: string; modo_desarrollo: boolean; enlace_desarrollo: string | null } | null>(null);

  const esFirebase = consulta.get('mode') === 'signIn' && consulta.has('oobCode') && consulta.has('apiKey');
  const errorEnlace = consulta.get('error') === 'enlace';

  useEffect(() => {
    if (!esFirebase) return;
    const guardado = correoGuardado();
    if (guardado) void completarFirebase(guardado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esFirebase]);

  async function completarFirebase(c: string) {
    setEnviando(true);
    setError(null);
    try {
      const r = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithEmailLink?key=${encodeURIComponent(consulta.get('apiKey')!)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: c, oobCode: consulta.get('oobCode') }),
      });
      const j = (await r.json()) as { idToken?: string; error?: { message?: string } };
      if (!r.ok || !j.idToken) throw new Error(`Firebase no aceptó el enlace (${j.error?.message ?? r.status}). Pide otro.`);
      await api.entrarFirebase(j.idToken);
      navegar('/', { reemplazar: true });
      await recargar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    const r = Correo.safeParse(correo);
    if (!r.success) {
      setErrorCampo('Escribe tu correo completo, por ejemplo nombre@solarix.mx.');
      return;
    }
    setErrorCampo(null);
    if (esFirebase) return void completarFirebase(r.data);
    setEnviando(true);
    setError(null);
    try {
      guardarCorreo(r.data);
      setRespuesta(await api.pedirEnlace(r.data));
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  async function entrarDesarrollo(enlace: string) {
    if (!transporteSimulado()) {
      window.location.href = enlace;
      return;
    }
    const token = new URL(enlace, window.location.origin).searchParams.get('token') ?? '';
    setEnviando(true);
    try {
      await api.entrarConToken(token);
      navegar('/', { reemplazar: true });
      await recargar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="entrar">
      <FranjaApiSimulada />
      {respuesta?.modo_desarrollo && (
        <div className="franja franja--desarrollo" role="status">
          <strong>MODO DESARROLLO</strong>
          <span className="franja__detalle"> · el enlace se enseña aquí en vez de mandarse por correo</span>
        </div>
      )}
      <div className="entrar__centro">
        <div className="entrar__marca">
          <Logotipo className="entrar__logo" />
          <h1 className="entrar__titulo">
            Cotizador
            <span>de Solarix Energy</span>
          </h1>
        </div>
        <div className="entrar__hoja">
          {respuesta ? (
            <div className="entrar__listo">
              <MailCheck className="entrar__icono" aria-hidden />
              <p className="entrar__mensaje">{respuesta.mensaje}</p>
              <p className="entrar__nota">Ábrelo en este mismo teléfono o computadora. Tu sesión dura 30 días.</p>
              {respuesta.enlace_desarrollo && (
                <Boton variante="principal" cargando={enviando} onClick={() => void entrarDesarrollo(respuesta.enlace_desarrollo!)}>
                  Entrar con el enlace (modo desarrollo)
                </Boton>
              )}
              <ErrorEnLinea error={error} />
              <button type="button" className="enlace-boton" onClick={() => setRespuesta(null)}>
                Usar otro correo
              </button>
            </div>
          ) : (
            <form onSubmit={(e) => void enviar(e)} noValidate>
              <h2 className="entrar__subtitulo">{esFirebase ? 'Confirma tu correo para terminar de entrar' : 'Entra con tu correo'}</h2>
              <p className="entrar__nota">
                {esFirebase ? 'Es el mismo correo al que llegó el enlace.' : 'Te mandamos un enlace para entrar. Sin contraseñas: solo correos dados de alta por Diego.'}
              </p>
              {errorEnlace && (
                <p className="entrar__aviso" role="alert">
                  Ese enlace ya se usó o venció. Pide otro.
                </p>
              )}
              <CampoTexto
                etiqueta="Correo"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="nombre@solarix.mx"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                error={errorCampo}
              />
              <ErrorEnLinea error={error} />
              <Boton type="submit" variante="principal" cargando={enviando} className="boton--ancho">
                {esFirebase ? 'Entrar' : 'Mandarme el enlace'}
              </Boton>
            </form>
          )}
        </div>
        <p className="entrar__pie">Uso interno de Solarix Energy · Hermosillo, Sonora</p>
      </div>
    </div>
  );
}
